import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Plus } from "lucide-react";

const STORAGE_KEY = "ios_install_banner_dismissed";

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandaloneMode(): boolean {
  return (
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true) ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isIOS()) return;
    if (isInStandaloneMode()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* ignore */ }
    // Small delay so the page loads first
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-sm"
        >
          <div className="rounded-2xl border bg-card shadow-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <img src="/icon-192x192.png" alt="MenuzAI" className="w-12 h-12 rounded-xl shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Instale o MenuzAI</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Acesse mais rápido pela tela inicial</p>
                </div>
              </div>
              <button
                onClick={dismiss}
                className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 rounded-xl bg-muted px-3 py-2.5 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
              <span>Toque em</span>
              <span className="inline-flex items-center gap-1 font-medium text-foreground">
                <Share className="h-3.5 w-3.5" /> Compartilhar
              </span>
              <span>e depois em</span>
              <span className="inline-flex items-center gap-1 font-medium text-foreground">
                <Plus className="h-3.5 w-3.5" /> Adicionar à Tela de Início
              </span>
            </div>
          </div>
          {/* Seta apontando para o botão de compartilhar no rodapé do Safari */}
          <div className="flex justify-center mt-1">
            <div className="w-4 h-4 bg-card border-b border-r border-border rotate-45 -mt-2.5 shadow-sm" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
